import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  CheckboxVisibility,
  DefaultButton,
  DetailsList,
  Dropdown,
  IColumn,
  IconButton,
  Modal,
  Pivot,
  PivotItem,
  PrimaryButton,
  TextField,
  SelectionMode
} from '@fluentui/react';
import * as Api from '../../../../api/src';
import ModalHeader from '../../../../control/modalHeader';
import { commonfn } from '../../../../common/commonfn';
import { ToastAppMsgContext } from '../../../../context/toastApp.Context';
import { ToastAppType } from '../../../../common/constant/enum/toastAppType';
import { DialogContext } from '../../../../context/dialog.context';
import { NoDataFound } from '../../../../control/noDataFound';
export interface ModaProductProps {
  IsOpen: boolean;
  ProductType: number | undefined;
  onCloseModal: () => void;
  onSaveClick?: () => void;
  Item?: Api.Product;
}
const ProductApi = new Api.ProductsApi();
const ProductTypesApi = new Api.ProductTypesApi();
const BrandsApi = new Api.BrandsApi();
const CategoriesApi = new Api.CategoriesApi();
const VendorsApi = new Api.VendorsApi();
const ComponentsApi = new Api.ComponentsApi();

const ModaProduct = ({ Item, ProductType, IsOpen, onCloseModal, onSaveClick }: ModaProductProps) => {
  const { t } = useTranslation();
  const [isShow, SetIsShow] = React.useState(false);
  const [detail, SetDetail] = React.useState<Api.ProductModel>({});
  const [specifications, setSpecifications] = React.useState<Api.ProductSpecificationResponseModel[]>([]);
  const [nameErrorMessage, SetNameErrorMessage] = React.useState('');
  const [productTypeErrorMessage, SetProductTypeErrorMessage] = React.useState('');
  const [CategoryErrorMessage, SetCategoryErrorMessage] = React.useState('');
  const [BrandErrorMessage, SetBrandErrorMessage] = React.useState('');
  const [VendorErrorMessage, SetVendorErrorMessage] = React.useState('');
  const [YearErrorMessage, SetYearErrorMessage] = React.useState('');
  const [listProductType, setListProductType] = React.useState<any[]>([]);
  const [listBrand, setListBrand] = React.useState<any[]>([]);
  const [listVendor, setListVendor] = React.useState<any[]>([]);
  const [listCategories, setListCategories] = React.useState<any[]>([]);
  const [listYears, setListYears] = React.useState<any[]>([]);
  const { setToastData } = React.useContext(ToastAppMsgContext);

  const getItemData = async () => {
    var data = (await ProductApi.apiProductsIdGet(Item?.Id || 0)).data;
    SetDetail(data.Data || { States: Api.StatesEnum.Active });
    setSpecifications(data.Data?.Specifications ?? [])
    SetIsShow(IsOpen);
  };

  const dropdownlistData = (brands: any[], textDefault: string) => {
    let result: any[] = [];
    if(textDefault)
      result.push({ key: 0, text: textDefault });
    brands?.forEach((element) => {
      result.push({ key: element.Id, text: element.Name });
    });
    if(!result.length)
      result.push({ key: null, text: t('NoDataFound'), disabled: true});
    return result;
  }

  const _columns = [
    {
      key: 'Component',
      name: t('Component'),
      fieldName: 'ComponentName',
      className: 'flex-center',
      minWidth: 200,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: 'ComponentBrand',
      name: t('Brand'),
      className: 'flex-center',
      minWidth: 200,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: Api.ProductSpecificationResponseModel) => {
        return (
          <Dropdown
            styles={{ root: { width: '100%' } }}
            options={dropdownlistData(item.Brands ?? [], t('AllBrands'))}
            selectedKey={item.CBrandId}
            onChange={(e, opt, index) => {
              const row = specifications.findIndex((c) => c === item);
              specifications[row].CBrandId = opt?.key as number;
              specifications[row].CModelId = 0;
              setSpecifications([...specifications]);
            }}
          ></Dropdown>
        );
      },
    },
    {
      key: 'ComponentModel',
      name: t('Model'),
      className: 'flex-center',
      isResizable: true,
      onRender: (item: Api.ProductSpecificationResponseModel) => {
        return (
          <Dropdown
            placeholder={t('Select')}
            styles={{ root: { width: '100%' } }}
            options={dropdownlistData(item.Models?.filter((c) => item.CBrandId === 0 || c.CBrandId === item.CBrandId) ?? [], '')}
            selectedKey={item?.CModelId}
            onChange={(e, opt, index) => {
              const row = specifications.findIndex((c) => c === item);
              specifications[row].CModelId = opt?.key as number;
              setSpecifications([...specifications]);
            }}
          ></Dropdown>
        );
      },
    },
  ] as IColumn[];
  React.useEffect(() => {
    if (IsOpen) {
      getAllProductTypes().then(() => {
        getYears();
        if (Item == null) {
          SetIsShow(IsOpen);
        } else {
          getItemData();
        }
      });
    } else {
      SetDetail({ States: Api.StatesEnum.Active, Year: new Date().getFullYear() });
    }
    SetNameErrorMessage('');
    SetProductTypeErrorMessage('');
    SetBrandErrorMessage('');
    SetCategoryErrorMessage('');
    SetYearErrorMessage('');
    SetVendorErrorMessage('');
  }, [IsOpen]);
  React.useEffect(() => {}, [detail]);
  const getTitle = (): string => {
    if (Item) {
      return t('EditProduct');
    }
    return t('NewProduct');
  };
  const onClose = () => {
    onCloseModal();
    SetIsShow(false);
  };
  const validateFormData = () => {
    let pass = true;
    if (!detail.Name) {
      SetNameErrorMessage(t('ModelNameIsRequired'));
      pass = false;
    }
    if (!detail.ProductTypeId) {
      SetProductTypeErrorMessage(t('IsRequired').replace('{0}', t('ProductType')));
      pass = false;
    }
    if (!detail.CategoryId) {
      SetCategoryErrorMessage(t('IsRequired').replace('{0}', t('Category')));
      pass = false;
    }
    if (!detail.BrandId) {
      SetBrandErrorMessage(t('IsRequired').replace('{0}', t('Brand')));
      pass = false;
    }
    if (!detail.Year) {
      SetYearErrorMessage(t('IsRequired').replace('{0}', t('Year')));
      pass = false;
    }
    if (!detail.VendorId) {
      SetVendorErrorMessage(t('IsRequired').replace('{0}', t('Vendor')));
      pass = false;
    }
    return pass;
  };
  React.useEffect(() => {
    getAll();
  }, [detail.ProductTypeId]);
  const getAll = async () => {
    await getAllCategories(detail.ProductTypeId || 0);
    await getAllVendors(detail.ProductTypeId || 0);
    await getAllBrands(detail.ProductTypeId || 0);
  };
  
  const onSave = async () => {
    if (!validateFormData()) {
      return;
    }

    let modelIds = specifications
      .filter((o) => o.CModelId ?? 0 > 0)
      .map((o) => {
        return o.CModelId || 0;
      });
    detail.ModelIds = modelIds;

    var data = await ProductApi.apiProductsPost(detail);
    if (!data || data.status !== 200) return;
    setToastData({ Message: t('Successed'), Type: ToastAppType.Success });
    onCloseModal();
    SetIsShow(false);
    if (onSaveClick) {
      onSaveClick();
    }
  };
  const getAllProductTypes = async () => {
    let productTypes: any[] = listProductType || [];
    if (productTypes.length === 0) {
      let data = (
        await ProductTypesApi.apiProductTypesPagedPost({
          PageNumber: 1,
          OrderBy: [{ ColumnName: 'Name' }],
          PageSize: 0,
          SearchString: '',
          GetOnlyActive: true,
        })
      ).data;
      data.Data?.forEach((element) => {
        productTypes.push({ key: element.Id, text: element.Name });
      });
      setListProductType(productTypes);
    }
    if (!productTypes.length) {
      productTypes.push({
        key: null,
        text: t('NoDataFound'),
        disabled: true,
      });
    }
    if (productTypes.length > 0 && Item == null) {
      SetDetail({
        ...detail,
        ProductTypeId: ProductType ? ProductType : productTypes[0].key,
        States: Api.StatesEnum.Active,
        Year: new Date().getFullYear(),
      });
    }
  };
  const getAllBrands = async (ProductTypeId: number) => {
    if (ProductTypeId == 0) {
      setListBrand([]);
      return;
    }
    let data = (
      await BrandsApi.apiBrandsPagedPost({
        PageNumber: 1,
        OrderBy: [{ ColumnName: 'Name' }],
        PageSize: 0,
        SearchString: '',
        GetOnlyActive: true,
        ProductTypeId: ProductTypeId,
      })
    ).data;
    let brands: any[] = [];
    data.Data?.forEach((element) => {
      brands.push({ key: element.Id, text: element.Name });
    });
    if (!brands.length) {
      brands.push({
        key: null,
        text: t('NoDataFound'),
        disabled: true,
      });
    }
    setListBrand(brands);
    if (brands.length) {
      return brands[0].key;
    }
  };
  const getAllCategories = async (ProductTypeId: number) => {
    if (ProductTypeId == 0) {
      setListCategories([]);
      return;
    }
    let data = (
      await CategoriesApi.apiCategoriesPagedPost({
        PageNumber: 1,
        OrderBy: [{ ColumnName: 'Name' }],
        PageSize: 0,
        SearchString: '',
        GetOnlyActive: true,
        ProductTypeId: ProductTypeId,
      })
    ).data;
    let categories: any[] = [];
    data.Data?.forEach((element) => {
      categories.push({ key: element.Id, text: element.Name });
    });

    if (!categories.length) {
      categories.push({
        key: null,
        text: t('NoDataFound'),
        disabled: true,
      });
    }
    setListCategories(categories);
    if (categories.length) {
      return categories[0].key;
    }
  };
  const getYears = async () => {
    if (listYears.length > 0) return;
    let years: any[] = [];
    commonfn.getListYears().forEach((element) => {
      years.push({ key: element, text: element.toString() });
    });
    setListYears(years);
  };
  const getAllVendors = async (ProductTypeId: number) => {
    if (ProductTypeId == 0) {
      setListVendor([]);
      return;
    }
    let data = (
      await VendorsApi.apiVendorsPagedPost({
        PageNumber: 1,
        OrderBy: [{ ColumnName: 'Name' }],
        PageSize: 0,
        SearchString: '',
        GetOnlyActive: true,
        ProductTypeId: ProductTypeId,
      })
    ).data;
    let vendors: any[] = [];
    data.Data?.forEach((element) => {
      vendors.push({ key: element.Id, text: element.Name });
    });
    if (!vendors.length) {
      vendors.push({
        key: null,
        text: t('NoDataFound'),
        disabled: true,
      });
    }
    setListVendor(vendors);
    if (vendors.length) {
      return vendors[0].key;
    }
  };
  
  return (
    <Modal isOpen={isShow} isModeless={true}>
      <ModalHeader
        title={getTitle()}
        onCloseModal={() => {
          onClose();
        }}
      />
      <div className={'modal-content'} style={{ width: 700 }}>
        <Pivot>
          <PivotItem headerText={ detail.Id ?? 0 > 0 ? t('ProductInfo') : ''} itemKey="ProductInfo">
            <div className="ms-Grid" dir="ltr" style={{marginTop: detail.Id ?? 0 > 0 ? 0 : -50}}>
              <div className="ms-Grid-row">
                <div className="ms-md12  ms-Grid-col padding-left-0 padding-right-0">
                  <TextField
                    label={t('ModelName') + ' (*)'}
                    autoFocus={true}
                    errorMessage={nameErrorMessage}
                    value={detail?.Name || ''}
                    onChange={(e, newValue) => {
                      SetDetail({ ...detail, Name: newValue });
                      SetNameErrorMessage('');
                    }}
                  ></TextField>
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-md6 padding-left-0 ms-Grid-col">
                  <TextField
                    label={t('GTIN')}
                    value={detail?.GTIN || ''}
                    onChange={(e, newValue) => {
                      SetDetail({ ...detail, GTIN: newValue });
                    }}
                  ></TextField>
                </div>
                <div className="ms-md6 padding-right-0 ms-Grid-col">
                  <TextField
                    label={t('ArticleNumber')}
                    value={detail?.ArticleNumber || ''}
                    onChange={(e, newValue) => {
                      SetDetail({ ...detail, ArticleNumber: newValue });
                    }}
                  ></TextField>
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-md6 padding-left-0 ms-Grid-col">
                  <Dropdown
                    options={listProductType}
                    label={t('ProductType') + ' (*)'}
                    disabled={Item != null}
                    errorMessage={productTypeErrorMessage}
                    placeholder={t('SelectAProductType')}
                    selectedKey={detail?.ProductTypeId || ''}
                    onChange={(e, opt, index) => {
                      if (opt?.key) {
                        SetDetail({ ...detail, ProductTypeId: parseInt(opt?.key.toString()) });
                        SetProductTypeErrorMessage('');
                      }
                    }}
                  ></Dropdown>
                </div>
                <div className="ms-md6 padding-right-0 ms-Grid-col">
                  <Dropdown
                    options={listCategories}
                    label={t('Category') + ' (*)'}
                    errorMessage={CategoryErrorMessage}
                    placeholder={t('SelectACategory')}
                    selectedKey={detail?.CategoryId || ''}
                    onChange={(e, opt, index) => {
                      if (opt?.key) {
                        SetDetail({ ...detail, CategoryId: parseInt(opt?.key.toString()) });
                      }
                      SetCategoryErrorMessage('');
                    }}
                  ></Dropdown>
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-md6 padding-left-0 ms-Grid-col">
                  <Dropdown
                    options={listBrand}
                    label={t('Brand') + ' (*)'}
                    errorMessage={BrandErrorMessage}
                    placeholder={t('SelectABrand')}
                    selectedKey={detail?.BrandId || ''}
                    onChange={(e, opt, index) => {
                      if (opt?.key) {
                        SetDetail({ ...detail, BrandId: parseInt(opt?.key.toString()) });
                      }
                      SetBrandErrorMessage('');
                    }}
                  ></Dropdown>
                </div>
                <div className="ms-md6 padding-right-0 ms-Grid-col">
                  <Dropdown
                    options={listYears}
                    label={t('ProductYear') + ' (*)'}
                    errorMessage={YearErrorMessage}
                    selectedKey={detail?.Year || ''}
                    onChange={(e, opt, index) => {
                      if (opt?.key) {
                        SetDetail({ ...detail, Year: parseInt(opt?.key.toString()) });
                      }
                      SetYearErrorMessage('');
                    }}
                  ></Dropdown>
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-md12 padding-left-0 padding-right-0  ms-Grid-col">
                  <Dropdown
                    options={listVendor}
                    label={t('Vendor') + ' (*)'}
                    errorMessage={VendorErrorMessage}
                    placeholder={t('SelectAVendor')}
                    selectedKey={detail?.VendorId || ''}
                    onChange={(e, opt, index) => {
                      if (opt?.key) {
                        SetDetail({ ...detail, VendorId: parseInt(opt?.key.toString()) });
                      }
                      SetVendorErrorMessage('');
                    }}
                  ></Dropdown>
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-md6 margin-0 padding-left-0 ms-Grid-col">
                  <Checkbox
                    label={t('Active')}
                    checked={detail?.States === Api.StatesEnum.Active ? true : false}
                    onChange={(e, checked) => {
                      SetDetail({ ...detail, States: checked ? Api.StatesEnum.Active : Api.StatesEnum.Inactive });
                    }}
                  ></Checkbox>
                </div>
              </div>
            </div>
          </PivotItem>
          {(detail.Id ?? 0 > 0) &&
          <PivotItem headerText={t('Specifications')} itemKey="Specifications">
            <DetailsList items={specifications} columns={_columns} checkboxVisibility={CheckboxVisibility.hidden} />
            <NoDataFound isShow={(detail.Specifications?.length || 0) === 0}></NoDataFound>
          </PivotItem>}
        </Pivot>
      </div>
      <div className={'modal-footer'}>
        <DefaultButton
          onClick={() => {
            onClose();
          }}
          className={'margin-left-10'}
        >
          {t('Close')}
        </DefaultButton>
        <PrimaryButton
          className={'margin-left-10'}
          onClick={() => {
            onSave();
          }}
        >
          {t('Save')}
        </PrimaryButton>
      </div>
    </Modal>
  );
};
export default ModaProduct;